import React from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useFigureBlob } from '../../hooks/useFigureBlob';
import type { FigureOptions } from './FigureExtension';

/**
 * Tiptap React NodeView for the custom <figure> node.
 *
 * Images are fetched via the authenticated API client (Authorization header).
 * The JWT is never embedded in a URL or persisted to the document HTML.
 * Blob object-URLs are created in memory and revoked on unmount.
 *
 * projectId is read from the extension's options, set via
 * FigureExtension.configure({ projectId }).
 */
const FigureNodeView: React.FC<NodeViewProps> = ({ node, extension, selected }) => {
  const { figureId, alt, caption, label } = node.attrs as {
    figureId: string | null;
    alt: string;
    caption: string;
    label: string;
  };
  const projectId = (extension.options as FigureOptions).projectId ?? '';
  const captionText = label || caption || '';

  const { blobUrl, error } = useFigureBlob(projectId, figureId);

  return (
    <NodeViewWrapper
      as="figure"
      className={`rda-figure${selected ? ' outline outline-2 outline-blue-400 outline-offset-2' : ''}`}
      data-figure-id={figureId ?? undefined}
    >
      {blobUrl ? (
        <img src={blobUrl} alt={alt || captionText || ''} className="rda-figure__img" />
      ) : error ? (
        <div className="inline-block px-3 py-2 text-xs text-red-500 border border-red-200 rounded bg-red-50">
          Failed to load figure
        </div>
      ) : (
        <div className="rda-figure__img inline-block w-32 h-20 bg-slate-100 rounded animate-pulse" />
      )}
      {captionText && (
        <figcaption className="rda-figure__caption">{captionText}</figcaption>
      )}
    </NodeViewWrapper>
  );
};

export default FigureNodeView;
